import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Download, Coins, TrendingUp, TrendingDown, Calendar, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const reasonLabel = (r) => ({
  welcome_trial: "🎁 Trial cortesia",
  admin_grant: "👤 Concedido pelo admin",
  admin: "👤 Admin",
  order_reserve: "🛒 Consumido em pedido",
  "refund:fulfillment_failed": "↩️ Refund (falha)",
}[r] || r);

const formatDate = (iso) => new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
const toISO = (d) => d ? new Date(d + "T00:00:00").toISOString() : "";

export default function ExtratoCreditosMineral() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ credited: 0, consumed: 0 });
  const [loading, setLoading] = useState(true);
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (since) q.set("since", toISO(since));
      if (until) q.set("until", toISO(until));
      const r = await api.get(`/mineral/credits/mine/ledger${q.toString() ? "?" + q.toString() : ""}`);
      setRows(r.data.rows || []);
      setSummary(r.data.summary || { credited: 0, consumed: 0 });
    } catch { toast.error("Falha ao carregar extrato"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportCSV = async () => {
    try {
      const token = localStorage.getItem("session_token") || "";
      const q = new URLSearchParams({ format: "csv" });
      if (since) q.set("since", toISO(since));
      if (until) q.set("until", toISO(until));
      const resp = await fetch(`${API}/api/mineral/credits/mine/ledger?${q.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include",
      });
      if (!resp.ok) throw new Error("CSV failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `extrato-hermes-mineral-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Extrato exportado");
    } catch { toast.error("Falha ao exportar CSV"); }
  };

  const balance = useMemo(() => summary.credited - summary.consumed, [summary]);

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="extrato-creditos-mineral">
      <div className="mb-6">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 mb-3">
          <ArrowLeft className="w-3.5 h-3.5"/> Voltar ao dashboard
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl inline-flex items-center justify-center"
               style={{ background: "linear-gradient(135deg, #0B1B2F, #071B34)" }}>
            <Coins className="w-5 h-5" style={{ color: "#F0D37A" }}/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Meus créditos Mineral</div>
            <h1 className="font-heading text-3xl font-semibold text-slate-900">Extrato financeiro</h1>
          </div>
        </div>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5 border border-emerald-200/60 shadow-sm" data-testid="summary-credited">
          <div className="flex items-center gap-2 text-emerald-700 mb-1">
            <TrendingUp className="w-4 h-4"/>
            <span className="text-[11px] uppercase tracking-widest font-semibold">Total creditado</span>
          </div>
          <div className="font-heading text-3xl font-bold text-slate-900">{summary.credited}</div>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-rose-200/60 shadow-sm" data-testid="summary-consumed">
          <div className="flex items-center gap-2 text-rose-700 mb-1">
            <TrendingDown className="w-4 h-4"/>
            <span className="text-[11px] uppercase tracking-widest font-semibold">Total consumido</span>
          </div>
          <div className="font-heading text-3xl font-bold text-slate-900">{summary.consumed}</div>
        </div>
        <div className="rounded-2xl p-5 shadow-sm text-white" data-testid="summary-balance"
             style={{ background: "linear-gradient(135deg,#0B1B2F,#071B34)" }}>
          <div className="flex items-center gap-2 mb-1" style={{ color: "#F0D37A" }}>
            <Coins className="w-4 h-4"/>
            <span className="text-[11px] uppercase tracking-widest font-semibold">Saldo líquido do período</span>
          </div>
          <div className="font-heading text-3xl font-bold">{balance}</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm mb-4" data-testid="extrato-filters">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-slate-500 font-semibold">De</label>
            <input type="date" value={since} onChange={(e) => setSince(e.target.value)}
              data-testid="filter-since"
              className="h-10 px-3 rounded-lg border border-slate-200 mt-1 text-sm"/>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Até</label>
            <input type="date" value={until} onChange={(e) => setUntil(e.target.value)}
              data-testid="filter-until"
              className="h-10 px-3 rounded-lg border border-slate-200 mt-1 text-sm"/>
          </div>
          <button onClick={load} data-testid="btn-apply-filter"
            className="h-10 px-4 rounded-lg text-sm font-medium text-white transition inline-flex items-center gap-1.5"
            style={{ background: "#0B1B2F" }}>
            <Calendar className="w-3.5 h-3.5"/> Aplicar
          </button>
          <div className="flex-1"/>
          <button onClick={exportCSV} data-testid="btn-export-csv"
            className="h-10 px-4 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 shadow-sm transition"
            style={{ background: "linear-gradient(180deg,#F5DE8C,#D4AF37)", color: "#0B1B2F" }}>
            <Download className="w-3.5 h-3.5"/> Exportar CSV
          </button>
        </div>
      </div>

      {/* Ledger */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400 text-sm">Carregando…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm" data-testid="extrato-empty">
            Sem movimentações no período.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map((r, i) => (
              <li key={i} className="p-4 flex items-center gap-3" data-testid={`extrato-row-${i}`}>
                {r.delta > 0
                  ? <TrendingUp className="w-4 h-4 text-emerald-500 shrink-0"/>
                  : <TrendingDown className="w-4 h-4 text-rose-500 shrink-0"/>}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{reasonLabel(r.reason)}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {formatDate(r.at)} · {r.product_code}{r.order_id ? ` · ${r.order_id}` : ""}
                  </div>
                </div>
                <div className={`font-bold text-sm shrink-0 ${r.delta > 0 ? "text-emerald-600" : "text-rose-600"}`}>
                  {r.delta > 0 ? "+" : ""}{r.delta}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
